import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import RuleIcon from "@mui/icons-material/Rule";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PersonIcon from "@mui/icons-material/Person";
import GavelIcon from "@mui/icons-material/Gavel";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import GroupsIcon from "@mui/icons-material/Groups";
import BalanceIcon from "@mui/icons-material/Balance";

import { PERMISSIONS, PQRSD_TYPES } from "../../constants";
import {
  subscribePendingApprovals,
  subscribeExpiredInfoRequests,
  subscribePendingAppeals,
} from "../../services/backend/PqrsdService";
import { listPqrsdGroups } from "../../services/backend/PqrsdConfigService";
import {
  selectInbox,
  selectMine,
  selectExpired,
  selectAppeals,
  groupOptions,
  filterByGroup,
  summarizeExpired,
  MY_GROUPS,
} from "./approvalQueueSelectors";

import { pqrsdText } from "./pqrsdLabel";
// Casos a revisar — everything in the PQRSD module that is waiting on a person's
// decision, read as an inbox: requerimientos de información waiting for someone
// to sign off, requerimientos the citizen let expire, and recursos de
// reposición waiting to be resolved.
//
// Every PQRSD user gets it, because both sides of the wait need to see it: the
// reviewer has something to act on, and the officer who submitted it needs to
// know their own is still sitting there. The entity's 10-day window to require
// keeps running the whole time a requerimiento waits here, which is exactly why
// it should not be something you discover by opening a PQRSD at random.
//
// The third tab is narrower. A requerimiento the citizen let expire leaves the
// PQRSD suspended with nothing scheduled to happen next: the sweep raises the
// flag and stops, because art. 17 wants a person to decide. That decision is the
// dependency supervisor's, so the tab only appears for whoever can decree the
// desistimiento, and it only lists the dependencies they actually lead.
//
// The fourth follows the third: once a desistimiento is decreed the citizen may
// file a recurso de reposición, and the PQRSD stays "en recurso" until whoever
// may decree a desistimiento resolves it — within two months (art. 86 CPACA).

const dateFmt = new Intl.DateTimeFormat("es-CO", {
  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
});

const formatDate = (value) => {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : dateFmt.format(d);
};

// Days left on the entity's own term, which is what makes an item urgent.
const daysLeft = (deadline) => {
  if (!deadline) return null;
  const d = deadline instanceof Date ? deadline : new Date(deadline);
  if (Number.isNaN(d.getTime())) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86400000);
};

// A tab label with its count as a red badge instead of a parenthesised number:
// the point of this page is that something is waiting, and "(3)" in the same
// grey as everything else does not say that. Zero shows nothing at all — an
// empty queue is not news.
const TabLabel = ({ text, count }) => (
  <Badge
    color="error"
    badgeContent={count}
    max={99}
    sx={{ "& .MuiBadge-badge": { position: "static", transform: "none", ml: 1.25 } }}
  >
    {text}
  </Badge>
);

export default function PqrsdApprovalQueue() {
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.value);
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("inbox");

  const [expiredRows, setExpiredRows] = useState(null);
  const [appealRows, setAppealRows] = useState(null);
  // The dependencies this user leads. null = not resolved yet.
  const [supervisedGroupIds, setSupervisedGroupIds] = useState(null);
  // Every dependency this user works in, leading it or not. Drives the default
  // filter, so null (not resolved) counts as loading: showing the whole entity's
  // queue for one frame and then snatching most of it away reads as a bug.
  const [myGroupIds, setMyGroupIds] = useState(null);
  // "" = todas, MY_GROUPS = mi dependencia, otherwise one dependency id.
  const [groupFilter, setGroupFilter] = useState("");
  // Set once the user picks for themselves, so the default never fights them.
  const filterTouched = useRef(false);

  const perms = user?.permissions || [];
  const canApprove = perms.includes(PERMISSIONS.PQRSD.APPROVE_RESPONSE);
  const canDecree = perms.includes(PERMISSIONS.PQRSD.DECREE_DESISTIMIENTO);
  const canSeeAll = perms.includes(PERMISSIONS.PQRSD.LIST);
  const myId = user?.userId || user?.userUID;

  useEffect(() => subscribePendingApprovals(setRows), []);

  // Only the people who can act on an expired requerimiento — or decide a
  // recurso — subscribe to them.
  useEffect(() => {
    if (!canDecree) return undefined;
    return subscribeExpiredInfoRequests(setExpiredRows);
  }, [canDecree]);
  useEffect(() => {
    if (!canDecree) return undefined;
    return subscribePendingAppeals(setAppealRows);
  }, [canDecree]);

  // Two different questions off one read: which dependencies this user leads
  // (that decides what they may see) and which they work in at all (that only
  // decides what the page shows them first).
  useEffect(() => {
    if (!myId) return undefined;
    let alive = true;
    (async () => {
      const groups = await listPqrsdGroups();
      if (!alive) return;
      const leads = (g) => Array.isArray(g.supervisorIds) && g.supervisorIds.includes(myId);
      const belongs = (g) => leads(g) || (Array.isArray(g.memberIds) && g.memberIds.includes(myId));
      setSupervisedGroupIds(groups.filter(leads).map((g) => g.id));
      setMyGroupIds(groups.filter(belongs).map((g) => g.id));
    })();
    return () => { alive = false; };
  }, [myId]);

  // Default to your own dependency once we know what it is. Someone opening
  // this page is looking for their own work; the entity's whole backlog is one
  // click away, not the other way round.
  useEffect(() => {
    if (filterTouched.current || !myGroupIds?.length) return;
    setGroupFilter(MY_GROUPS);
  }, [myGroupIds]);

  const chooseGroup = (value) => {
    filterTouched.current = true;
    setGroupFilter(value);
  };

  const inbox = useMemo(
    () => selectInbox(rows, { myId, canApprove, supervisedGroupIds }),
    [rows, myId, canApprove, supervisedGroupIds]
  );
  // Leading a dependency is enough to approve its requerimientos.
  const canReview = canApprove || (supervisedGroupIds || []).length > 0;
  const mine = useMemo(() => selectMine(rows, { myId }), [rows, myId]);
  // Two steps, deliberately: what this user may see, then what they chose to
  // look at. The tab count stays on the first so the filter never hides the
  // fact that something is waiting elsewhere.
  const expired = useMemo(
    () => selectExpired(expiredRows, { canSeeAll, supervisedGroupIds }),
    [expiredRows, canSeeAll, supervisedGroupIds]
  );
  // A dependency supervisor with no dependency recorded yet would otherwise get
  // a permanently empty tab with no explanation.
  const showExpiredTab = canDecree && (canSeeAll || supervisedGroupIds === null || supervisedGroupIds.length > 0);
  const appeals = useMemo(
    () => selectAppeals(appealRows, { canSeeAll, supervisedGroupIds, myId }),
    [appealRows, canSeeAll, supervisedGroupIds, myId]
  );
  // Also shown to an assignee who leads no dependency, as soon as one of their
  // own PQRSDs has a recurso to resolve.
  const showAppealsTab = canDecree && (showExpiredTab || appeals.length > 0);
  const activeTab =
    (tab === "expired" && !showExpiredTab) || (tab === "appeals" && !showAppealsTab) ? "inbox" : tab;
  const scoped =
    activeTab === "expired" ? expired
    : activeTab === "appeals" ? appeals
    : activeTab === "inbox" ? inbox
    : mine;
  const groups = useMemo(() => groupOptions(scoped), [scoped]);
  const shown = useMemo(
    () => filterByGroup(scoped, groupFilter, { myGroupIds: myGroupIds || [] }),
    [scoped, groupFilter, myGroupIds]
  );
  const loading =
    myGroupIds === null ||
    (activeTab === "expired" ? expiredRows === null : activeTab === "appeals" ? appealRows === null : rows === null);
  // What the filter is keeping off screen. A default that quietly hides another
  // dependency's expired requerimientos would be worse than no default at all:
  // those are the ones with a term already run out and nobody looking.
  const elsewhere = scoped.length - shown.length;
  // Only a supervisor who can see every dependency has an entity-wide total to
  // state; for anyone else "3" already means "3 in my dependency".
  const expiredSummary = useMemo(() => summarizeExpired(expired), [expired]);

  // A dependency that empties out while selected would otherwise leave the user
  // staring at "no hay requerimientos" with no hint that the filter did it.
  // "Mi dependencia" is exempt: it is the default, and it means something even
  // on a tab where nothing of yours is waiting.
  useEffect(() => {
    if (!groupFilter || groupFilter === MY_GROUPS) return;
    if (!groups.some((g) => g.id === groupFilter)) setGroupFilter("");
  }, [groups, groupFilter]);

  // An expired requerimiento reads differently from one waiting on a signature:
  // there is no reviewer and no term left to protect, only a file parked since a
  // date that has passed and the decision nobody has taken on it yet.
  const ExpiredRow = ({ item }) => {
    const overdue = -(daysLeft(item.citizenDeadline) ?? 0);
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 1,
          cursor: "pointer",
          borderColor: "error.light",
          "&:hover": { borderColor: "error.main", bgcolor: "action.hover" },
        }}
        onClick={() => navigate(`/pqrsd/admin/${encodeURIComponent(item.radicado)}`)}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
                {item.radicado}
              </Typography>
              {PQRSD_TYPES[item.type] && (
                <Chip size="small" variant="outlined" label={PQRSD_TYPES[item.type]} />
              )}
              <Chip
                size="small"
                color="error"
                icon={<ScheduleIcon />}
                label={
                  overdue > 0
                    ? `Venció hace ${overdue} día${overdue === 1 ? "" : "s"}`
                    : "Plazo vencido"
                }
              />
            </Stack>
            {item.subject && (
              <Typography variant="body2" sx={{ mt: 0.5 }} noWrap>
                {item.subject}
              </Typography>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
              <GroupsIcon fontSize="inherit" color="disabled" />
              <Typography variant="caption" color="text.secondary">
                {item.groupName || "Sin dependencia"} · plazo hasta {formatDate(item.citizenDeadline)}
              </Typography>
            </Stack>
          </Box>
          <Button size="small" variant="contained" color="error" startIcon={<GavelIcon />}>
            Auditar
          </Button>
        </Stack>
      </Paper>
    );
  };

  // A recurso reads by the deadline to decide it: two months from filing, after
  // which the entity's silence counts as a denial (art. 86 CPACA).
  const AppealRow = ({ item }) => {
    const left = daysLeft(item.decisionDueAt);
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 1,
          cursor: "pointer",
          borderColor: left != null && left <= 7 ? "warning.main" : undefined,
          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
        }}
        onClick={() => navigate(`/pqrsd/admin/${encodeURIComponent(item.radicado)}`)}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
                {item.radicado}
              </Typography>
              {PQRSD_TYPES[item.type] && (
                <Chip size="small" variant="outlined" label={PQRSD_TYPES[item.type]} />
              )}
              {left != null && (
                <Chip
                  size="small"
                  icon={<ScheduleIcon />}
                  color={left < 0 ? "error" : left <= 7 ? "warning" : "default"}
                  variant={left <= 7 ? "filled" : "outlined"}
                  label={
                    left < 0
                      ? `Venció hace ${Math.abs(left)} día${Math.abs(left) === 1 ? "" : "s"}`
                      : `Resolver en ${left} día${left === 1 ? "" : "s"}`
                  }
                />
              )}
            </Stack>
            {item.subject && (
              <Typography variant="body2" sx={{ mt: 0.5 }} noWrap>
                {item.subject}
              </Typography>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
              <GroupsIcon fontSize="inherit" color="disabled" />
              <Typography variant="caption" color="text.secondary">
                {item.groupName || "Sin dependencia"} · interpuesto {formatDate(item.appealFiledAt)}
                {item.desistimientoFiledId ? ` · contra ${item.desistimientoFiledId}` : ""}
              </Typography>
            </Stack>
          </Box>
          <Button size="small" variant="contained" startIcon={<BalanceIcon />}>
            Resolver
          </Button>
        </Stack>
      </Paper>
    );
  };

  const Row = ({ item }) => {
    const left = daysLeft(item.slaDeadline);
    return (
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 1,
          cursor: "pointer",
          "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
        }}
        onClick={() => navigate(`/pqrsd/admin/${encodeURIComponent(item.radicado)}`)}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          justifyContent="space-between"
          alignItems={{ sm: "center" }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle2" sx={{ fontFamily: "monospace" }}>
                {item.radicado}
              </Typography>
              {PQRSD_TYPES[item.type] && (
                <Chip size="small" variant="outlined" label={PQRSD_TYPES[item.type]} />
              )}
              {left != null && (
                <Chip
                  size="small"
                  icon={<ScheduleIcon />}
                  color={left < 0 ? "error" : left <= 3 ? "warning" : "default"}
                  variant={left <= 3 ? "filled" : "outlined"}
                  label={
                    left < 0
                      ? `Vencida hace ${Math.abs(left)} día${Math.abs(left) === 1 ? "" : "s"}`
                      : `Quedan ${left} día${left === 1 ? "" : "s"}`
                  }
                />
              )}
            </Stack>
            {item.subject && (
              <Typography variant="body2" sx={{ mt: 0.5 }} noWrap>
                {item.subject}
              </Typography>
            )}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }} flexWrap="wrap">
              <PersonIcon fontSize="inherit" color="disabled" />
              <Typography variant="caption" color="text.secondary">
                {item.submittedByName || "Un funcionario"} · enviado {formatDate(item.submittedAt)}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
              <GroupsIcon fontSize="inherit" color="disabled" />
              <Typography variant="caption" color="text.secondary">
                {item.groupName || "Sin dependencia"}
              </Typography>
            </Stack>
          </Box>
          <Button size="small" variant="contained">
            {activeTab === "inbox" ? "Revisar" : "Abrir"}
          </Button>
        </Stack>
      </Paper>
    );
  };

  return (
    // lg, not md: four tabs with their badges do not fit in md, and the last one
    // (Recursos por resolver) was cut off behind a scroll arrow.
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <RuleIcon color="primary" />
        <Typography variant="h5">Casos a revisar</Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {activeTab === "appeals" ? (
          <>
            {pqrsdText("PQRSD cuyo ciudadano interpuso ")}<strong>recurso de reposición</strong> contra el acto que
            decretó el desistimiento, aportando la información que se le había requerido. Resuélvalo
            mediante decisión motivada —reponer y continuar el trámite, o confirmar el archivo— dentro
            de los dos meses siguientes a su presentación (artículo 86 de la Ley 1437 de 2011).
          </>
        ) : activeTab === "expired" ? (
          <>
            {pqrsdText("Requerimientos que el ciudadano dejó vencer sin responder. La PQRSD sigue")}{" "}
            <strong>suspendida</strong> hasta que usted decida: decretar el desistimiento tácito
            mediante acto motivado, o retirar el requerimiento y continuar el trámite. El sistema no
            archiva por su cuenta.
            {canSeeAll && expiredSummary.total > 0 && (
              <>
                {" "}
                En toda la entidad hay <strong>{expiredSummary.total}</strong>{" "}
                {expiredSummary.total === 1 ? "requerimiento vencido" : "requerimientos vencidos"} en{" "}
                {expiredSummary.dependencies}{" "}
                {expiredSummary.dependencies === 1 ? "dependencia" : "dependencias"}:{" "}
                {expiredSummary.breakdown.map((g) => `${g.name} (${g.count})`).join(", ")}.
              </>
            )}
          </>
        ) : (
          <>
            Requerimientos de información redactados y pendientes de que alguien los apruebe.
            Mientras uno espera aquí, <strong>el término de la entidad sigue corriendo</strong> y al
            ciudadano todavía no se le ha requerido nada.
          </>
        )}
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        <Tab value="inbox" label={<TabLabel text="Por revisar" count={inbox.length} />} />
        <Tab value="mine" label={<TabLabel text="Enviados por mí" count={mine.length} />} />
        {showExpiredTab && (
          <Tab
            value="expired"
            label={<TabLabel text="Requerimientos vencidos" count={expired.length} />}
            sx={{ color: expired.length ? "error.main" : undefined }}
          />
        )}
        {showAppealsTab && (
          <Tab
            value="appeals"
            label={<TabLabel text="Recursos por resolver" count={appeals.length} />}
          />
        )}
      </Tabs>

      <Divider sx={{ mb: 2 }} />

      {(groups.length > 1 || (myGroupIds || []).length > 0) && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap">
          <TextField
            select
            size="small"
            label="Dependencia"
            value={groupFilter}
            onChange={(e) => chooseGroup(e.target.value)}
            // "" is "Todas las dependencias" — without these the select reads
            // as empty instead of showing that option.
            SelectProps={{ displayEmpty: true }}
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 280 }}
          >
            {(myGroupIds || []).length > 0 && (
              <MenuItem value={MY_GROUPS}>
                Mi dependencia ({filterByGroup(scoped, MY_GROUPS, { myGroupIds: myGroupIds || [] }).length})
              </MenuItem>
            )}
            <MenuItem value="">Todas las dependencias ({scoped.length})</MenuItem>
            {groups.map((g) => {
              const count = filterByGroup(scoped, g.id).length;
              return (
                <MenuItem key={g.id} value={g.id}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    {/* Which dependency it is waiting in, visible without
                        opening each one in turn. */}
                    {activeTab === "expired" && count > 0 && (
                      <Box
                        sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "error.main" }}
                      />
                    )}
                    <span>{g.name} ({count})</span>
                  </Stack>
                </MenuItem>
              );
            })}
          </TextField>
          {elsewhere > 0 && (
            <Chip
              size="small"
              color="error"
              variant={activeTab === "expired" ? "filled" : "outlined"}
              icon={<ErrorOutlineIcon />}
              onClick={() => chooseGroup("")}
              label={
                elsewhere === 1
                  ? "1 en otra dependencia"
                  : `${elsewhere} en otras dependencias`
              }
            />
          )}
          {groupFilter && elsewhere === 0 && (
            <Button size="small" onClick={() => chooseGroup("")}>Ver todas</Button>
          )}
        </Stack>
      )}

      {loading && (
        <Typography variant="body2" color="text.secondary">Cargando…</Typography>
      )}

      {!loading && shown.length === 0 && (
        <Alert severity="success">
          {scoped.length > 0
            ? // Something is waiting, just not where the filter is pointing.
              "No hay nada en esta dependencia. Cambia el filtro para ver el resto."
            : activeTab === "appeals"
            ? "No hay recursos de reposición pendientes de decisión."
            : activeTab === "expired"
            ? canSeeAll
              ? "No hay requerimientos vencidos en ninguna dependencia."
              : "No hay requerimientos vencidos en tu dependencia."
            : activeTab === "inbox"
            ? canReview
              ? "No hay requerimientos esperando tu revisión."
              : "No tienes permiso de aprobación, así que no hay nada por revisar aquí. Revisa la pestaña «Enviados por mí»."
            : "No tienes requerimientos esperando aprobación."}
        </Alert>
      )}

      {shown.map((item) =>
        activeTab === "expired"
          ? <ExpiredRow key={item.radicado} item={item} />
          : activeTab === "appeals"
          ? <AppealRow key={item.radicado} item={item} />
          : <Row key={item.radicado} item={item} />
      )}
    </Container>
  );
}
